import { useState, useEffect, useRef, useCallback } from "react";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { X, Plus, Tag } from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function CustomerTags({ customerId, initialTags = [], onTagsChange }) {
  const [tags, setTags] = useState(initialTags);
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  // Update tags when initialTags change
  useEffect(() => {
    setTags(initialTags || []);
  }, [initialTags]);

  // Fetch tag suggestions
  const fetchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 1) {
      // Show recent tags when no query
      try {
        const response = await fetch(`${API}/tags/recent?limit=10`, {
          credentials: "include"
        });
        if (response.ok) {
          const data = await response.json();
          // Filter out already added tags
          const filtered = (data.tags || []).filter(t => !tags.includes(t));
          setSuggestions(filtered);
        }
      } catch (error) {
        console.error("Error fetching recent tags:", error);
      }
      return;
    }

    try {
      const response = await fetch(`${API}/tags/search?q=${encodeURIComponent(query)}&limit=10`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        // Filter out already added tags
        const filtered = (data.tags || []).filter(t => !tags.includes(t));
        setSuggestions(filtered);
      }
    } catch (error) {
      console.error("Error fetching tag suggestions:", error);
    }
  }, [tags]);

  // Debounced suggestion fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      if (showSuggestions) {
        fetchSuggestions(inputValue);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [inputValue, showSuggestions, fetchSuggestions]);

  // Close suggestions on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addTag = async (tagToAdd) => {
    const normalized = tagToAdd.toLowerCase().trim();
    
    if (!normalized) return;
    if (tags.includes(normalized)) {
      toast.error("Tag already exists");
      return;
    }
    if (tags.length >= 20) {
      toast.error("Maximum 20 tags per customer");
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/customers/${customerId}/tags`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ add: [normalized], remove: [] })
      });
      
      if (response.ok) {
        const data = await response.json();
        setTags(data.tags);
        setInputValue("");
        setShowSuggestions(false);
        if (onTagsChange) onTagsChange(data.tags);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to add tag");
      }
    } catch (error) {
      toast.error("Failed to add tag");
    } finally {
      setLoading(false);
    }
  };

  const removeTag = async (tagToRemove) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/customers/${customerId}/tags`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ add: [], remove: [tagToRemove] })
      });
      
      if (response.ok) {
        const data = await response.json();
        setTags(data.tags);
        if (onTagsChange) onTagsChange(data.tags);
      } else {
        toast.error("Failed to remove tag");
      }
    } catch (error) {
      toast.error("Failed to remove tag");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  return (
    <div ref={containerRef} className="relative" data-testid="customer-tags">
      {/* Existing Tags */}
      <div className="flex flex-wrap gap-2 mb-2">
        {tags.map((tag) => (
          <Badge
            key={tag}
            variant="secondary"
            className="bg-zinc-100 text-zinc-700 pl-2 pr-1 py-1 text-xs flex items-center gap-1"
            data-testid={`tag-${tag}`}
          >
            <Tag size={12} className="text-zinc-400" />
            {tag}
            <button
              onClick={() => removeTag(tag)}
              disabled={loading}
              className="ml-1 hover:bg-zinc-200 rounded p-0.5 transition-colors"
              data-testid={`remove-tag-${tag}`}
            >
              <X size={12} />
            </button>
          </Badge>
        ))}
        
        {tags.length === 0 && (
          <span className="text-xs text-zinc-400">No tags</span>
        )}
      </div>

      {/* Add Tag Input */}
      <div className="relative">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Plus size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onFocus={() => { setShowSuggestions(true); fetchSuggestions(inputValue); }}
              onKeyDown={handleKeyDown}
              placeholder="Add tag..."
              className="pl-7 h-8 text-sm border-2"
              disabled={loading || tags.length >= 20}
              data-testid="add-tag-input"
            />
          </div>
        </div>

        {/* Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div 
            className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-zinc-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto"
            data-testid="tag-suggestions"
          >
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => addTag(suggestion)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-zinc-50 flex items-center gap-2 transition-colors"
                data-testid={`suggestion-${suggestion}`}
              >
                <Tag size={14} className="text-zinc-400" />
                {suggestion}
              </button>
            ))}
          </div>
        )}
        
        {/* Show "Create new" option if typed value doesn't exist */}
        {showSuggestions && inputValue.trim() && !suggestions.includes(inputValue.toLowerCase().trim()) && (
          <div 
            className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-zinc-200 rounded-lg shadow-lg z-50"
            data-testid="create-tag-option"
          >
            <button
              onClick={() => addTag(inputValue)}
              className="w-full px-3 py-2 text-left text-sm hover:bg-zinc-50 flex items-center gap-2 text-[#FF6600]"
            >
              <Plus size={14} />
              Create tag &ldquo;{inputValue.toLowerCase().trim()}&rdquo;
            </button>
            {suggestions.map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => addTag(suggestion)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-zinc-50 flex items-center gap-2 transition-colors border-t border-zinc-100"
                data-testid={`suggestion-${suggestion}`}
              >
                <Tag size={14} className="text-zinc-400" />
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Tag count indicator */}
      {tags.length > 0 && (
        <p className="text-[10px] text-zinc-400 mt-1">
          {tags.length}/20 tags
        </p>
      )}
    </div>
  );
}
